import { CoherencePanel } from '@coherence-design-system/controls';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { Link } from '../../Share.styled';
import * as React from 'react';
import { HelpContext } from '../../../shared/HelpContext';
import { QuestionTitle } from './Help.styled';

export interface IHelpPanelProps {
  questionId: string;
}

export const HelpPanel = React.memo(function HelpPanelContent(
  props: IHelpPanelProps
): React.ReactElement {
  const { telemetryClient } = React.useContext(
    Context as React.Context<IEmployeeExperienceContext>
  );
  const { questionId } = props;

  return (
    <HelpContext.Consumer>
      {(context) => (
        <>
          <CoherencePanel
            backButton={{
              ariaLabel: 'Go back',
              onClick: () => {
                context.toggleShowHelpContextPanel(false);
              },
            }}
            onDismiss={() => {
              context.toggleShowHelpContextPanel(false);
            }}
            isOpen={context.showHelpContextPanel}
            customWidth={`305px`}
            isLightDismiss={true}
            isBlocking={false}
            titleText={'Help'}
            closeButtonAriaLabel="Close panel"
            hasCloseButton={true}
            overlayProps={{
              isDarkThemed: true,
            }}
            telemetryHook={telemetryClient}
          >
            <QuestionTitle>
              {
                {
                  q1: 'When will I receive my letter?',
                  q2: 'Where can I find the letter after it has been generated?',
                  q3: 'The information in the letter is not correct. How can I update my personal data?',
                  q4: 'I need a different type of letter (for business travel or something else that’s not offered here). How can I get that?',
                }[questionId]
              }
            </QuestionTitle>
            {
              {
                q1: (
                  <>
                    <p>
                      After you select a letter type, you can see when the letter will be delivered.
                      There are two ways:{' '}
                    </p>
                    <ol>
                      <li>
                        Directly: Your letter will be automatically created and downloaded to your
                        device. You can open and print it from the downloads folder of your browser.{' '}
                      </li>
                      <li>
                        Through AskHR: Your request needs to be reviewed and completed by AskHR.
                        Your prepared letter can be picked up once you are notified, usually within
                        2 business days. Use the My Cases dashboard to see the status of your
                        request.{' '}
                      </li>
                    </ol>
                  </>
                ),
                q2: (
                  <>
                    <p>
                      Your letter will be either available for download to your device or mailed to
                      you (if an actual signature is required).{' '}
                    </p>
                  </>
                ),
                q3: (
                  <>
                    <p>
                      To ensure that your personal information is displayed correctly in your
                      letter, go to{' '}
                      <Link
                        to={__Employee_Central_Link__}
                        title="Employee Central"
                        target="_blank"
                        usageEvent={{
                          feature: 'EVL',
                          subFeature: 'HelpPanel.EmployeeCentralLink',
                        }}
                      >
                        Employee Central (EC)
                      </Link>{' '}
                      My Profile. You can also get there by going back to HRweb with the left-hand
                      menu click HR… select Workplace resources View/change personal information
                      (Employee Central) in the menu above. Review your information and update it if
                      necessary.{' '}
                    </p>
                  </>
                ),
                q4: (
                  <>
                    <ol>
                      <li>
                        For business travel, visit the {' '}
                        <Link
                          to={__Btl_Link__}
                          title="Business Travel Letter tool"
                          target="_blank"
                          usageEvent={{
                            feature: 'EVL',
                            subFeature: 'HelpPanel.BusinessTravelLetterLink',
                          }}
                        >
                          Business Travel Letter (BTL)
                        </Link>{' '}
                        tool. If you are applying for a mortgage or loan, submit your bank&apos;s
                        form with your online request via the {' '}
                        <Link
                          to={__Payroll_Link__}
                          title="Payroll portal"
                          target="_blank"
                          usageEvent={{
                            feature: 'EVL',
                            subFeature: 'HelpPanel.PayrollLink',
                          }}
                        >
                          Payroll portal
                        </Link>{' '}
                        . You are responsible for filling in all the personal and employment details
                        on the form. The payroll provider will fill in payroll data and mail it back
                        to you.{' '}
                      </li>
                      <li>
                        If you are unsure where to go, use HRweb to find further information or
                        reach out to AskHR if direct support is needed.{' '}
                      </li>
                    </ol>
                  </>
                ),
              }[questionId]
            }
          </CoherencePanel>
        </>
      )}
    </HelpContext.Consumer>
  );
});
